<template>
  <div>
    <div>
      <FilterManager
        v-model="filters"
        :filters="filterSettings"
        @academicYear="onAcademicYearChanged"
        @center="onCenterChanged"
        @municipality="onMunicipalityChanged"
        @school="onSchoolChanged"
        @status="onStatusChanged"
        @search="onSearch"
      />
    </div>
    <div>
      <CustomResultNotFound
        v-if="!results"
        class="mt-4 row pt-3"
        text="No se ha encontrado resultados."
      />
      <div v-else>
        <ResultRow
          v-for="item in results"
          :key="item.id"
          :value="item"
          :assistants="item.assignmentOptions"
          @cancel="cancelRequest"
          @vacant-position="setAsVacantPosition"
          @pending-amendment="setAsPendingAmendment"
          @assign-assistant="assignAssistant"
          @change-to-pending-assign="changeToPendingAssign"
        />
        <pagination
          v-if="pagination.totalItemCount > pagination.perPage"
          :data="pagination"
          @pagination-go-page="onPageChanged"
        />
      </div>
    </div>
  </div>
</template>

<script>
import FilterManager from 'FilterManager';
import CustomResultNotFound from 'custom-result-not-found';
import ResultRow from './NecesityResultRow.vue';
import Pagination from 'pagination';
import locationService from '@/services/api/location.api.js';
import mixAlert from '@/mixin/alert.mixin';

import { AssignmentTypeEnum } from '@/modules/peiSAEE/assignment-type-enum.js';
import { Saee10StatusEnum } from '@/modules/peiSAEE/saee10-status-enum.js';
import {
  GetAssistantList,
  GetRequest,
  CancelRequest,
  SetAsVacantPosition,
  SetAsPendingAmendment,
  AssignAssistant,
  GetNecesitiesByYear,
  ChangeToPendingAssign,
} from '@/modules/peiSAEE/api.js';

const FIRST_PAGE = 1;
const FILTERS_KEY = 'irregularNecesitiesFiltersStorage';
const PAGINATION_KEY = 'irregularNecesitiesPaginationStorage';
const ASSIGNMENT_STATUS_OPTIONS = [
  { id: 'PENDING', name: 'Pendiente' },
  { id: 'ATTENDED', name: 'Atendido' },
];

export default {
  name: 'IrregularNecesities',
  mixins: [mixAlert],
  components: {
    FilterManager,
    CustomResultNotFound,
    ResultRow,
    Pagination,
  },
  props: {
    collections: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      filters: {
        academicYear: null,
        status: null,
        searchField: null,
        center: null,
        municipality: null,
        school: null,
        requestType: null,
        necesity: null,
        ubicationAlternative: null,
        route: null,
        assistant: null,
        assignmentStatus: null,
        assignmentType: null,
      },
      centers: [],
      municipalities: [],
      schools: [],
      assistants: [],
      results: [],
      pagination: {
        perPage: 10,
        currentPage: 1,
        totalItemCount: 0,
        pageCount: 0,
        count: 0,
      },
    };
  },
  computed: {
    filterSettings() {
      return [
        {
          name: 'academicYear',
          component: 'CustomDropDown',
          options: this.collections.academicYears,
          value: this.filters.academicYear,
          clearable: false,
          placeholder: 'Año Académico',
          fieldName: 'description',
          fieldKey: 'id',
          class: 'col-sm-12 col-md-3 mb-md-0 mb-2',
        },
        {
          name: 'status',
          component: 'CustomDropDown',
          options: this.collections.necesitiesStatus,
          value: this.filters.status,
          placeholder: 'Estatus',
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-sm-12 col-md-3 mb-md-0 mb-2',
        },
        {
          name: 'assignmentType',
          component: 'CustomDropDown',
          options: this.collections.assignmentTypes,
          value: this.filters.assignmentType,
          placeholder: 'Tipo de Asignación',
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-sm-12 col-md-3 mb-md-0 mb-2',
          visible: this.isPendingAssignmentStatusSelected,
        },
        {
          name: 'searchField',
          component: 'CustomInput',
          value: this.filters.searchField,
          placeholder:
            'Escriba Número de SIE, Registro, SAEE10, Solicitud o Nombre del Estudiante',
          icon: 'fa fa-search orange-icon',
          leftIcon: true,
          class: `col-sm-12 ${
            this.isPendingAssignmentStatusSelected ? 'col-md-3' : 'col-md-6'
          }`,
          type: 'text',
        },
        {
          name: 'center',
          label: 'Centro:',
          component: 'CustomDropDown',
          options: this.centers,
          value: this.filters.center,
          placeholder: 'Seleccione',
          class: 'col-12 col-sm-6 col-md-3 d-flex flex-column',
          fieldName: 'name',
          fieldKey: 'id',
          mapParentValue: this.filters.center == null,
          isAdvanced: true,
          disabled: false,
        },
        {
          name: 'municipality',
          label: 'Municipio:',
          component: 'CustomDropDown',
          options: this.municipalities,
          value: this.filters.municipality,
          placeholder: 'Seleccione',
          class: 'col-12 col-sm-6 col-md-3 d-flex flex-column',
          fieldName: 'name',
          fieldKey: 'id',
          mapParentValue: this.filters.municipality == null,
          isAdvanced: true,
          disabled: false,
        },
        {
          name: 'school',
          label: 'Escuela:',
          component: 'CustomDropDown',
          options: this.schools,
          value: this.filters.school,
          placeholder: 'Seleccione',
          class: 'col-12 col-sm-6 col-md-3 d-flex flex-column',
          fieldName: 'name',
          fieldKey: 'id',
          mapParentValue: this.filters.school == null,
          isAdvanced: true,
          disabled: false,
        },
        {
          name: 'requestType',
          label: 'Tipo de Solicitud:',
          component: 'CustomDropDown',
          options: this.collections.requestTypes,
          value: this.filters.determination,
          placeholder: 'Seleccione',
          class: 'col-12 col-sm-6 col-md-3 d-flex flex-column',
          fieldName: 'name',
          fieldKey: 'id',
          mapParentValue: this.filters.requestType == null,
          isAdvanced: true,
          disabled: false,
        },
        {
          name: 'necesity',
          label: 'Necesidades:',
          component: 'CustomDropDown',
          options: this.collections.necesities,
          value: this.filters.necesity,
          placeholder: 'Seleccione',
          class: 'col-12 col-sm-6 col-md-3 d-flex flex-column',
          fieldName: 'name',
          fieldKey: 'id',
          mapParentValue: this.filters.necesity == null,
          isAdvanced: true,
          disabled: false,
        },
        {
          name: 'ubicationAlternative',
          label: 'Alternativas de Ubicación:',
          component: 'CustomDropDown',
          options: this.collections.ubicationAlternatives,
          value: this.filters.ubicationAlternative,
          placeholder: 'Seleccione',
          class: 'col-12 col-sm-6 col-md-3 d-flex flex-column',
          fieldName: 'name',
          fieldKey: 'id',
          mapParentValue: this.filters.ubicationAlternative == null,
          isAdvanced: true,
          disabled: false,
        },
        {
          name: 'route',
          label: 'Ruta:',
          component: 'CustomDropDown',
          options: this.collections.routes,
          value: this.filters.route,
          placeholder: 'Seleccione',
          class: 'col-12 col-sm-6 col-md-3 d-flex flex-column',
          fieldName: 'name',
          fieldKey: 'id',
          mapParentValue: this.filters.route == null,
          isAdvanced: true,
          disabled: false,
        },
        {
          name: 'assistant',
          label: 'Asistente:',
          component: 'CustomDropDown',
          options: this.assistants,
          value: this.filters.assistant,
          placeholder: 'Seleccione',
          class: 'col-12 col-sm-6 col-md-3 d-flex flex-column',
          fieldName: 'name',
          fieldKey: 'id',
          mapParentValue: this.filters.assistant == null,
          isAdvanced: true,
          disabled: false,
        },
        {
          name: 'assignmentStatus',
          label: 'Asignación:',
          component: 'CustomDropDown',
          options: ASSIGNMENT_STATUS_OPTIONS,
          value: this.filters.assignmentStatus,
          placeholder: 'Seleccione',
          class: 'col-12 col-sm-6 col-md-3 d-flex flex-column',
          fieldName: 'name',
          fieldKey: 'id',
          mapParentValue: this.filters.assignmentStatus == null,
          isAdvanced: true,
          disabled: false,
        },
      ];
    },
    isPendingAssignmentStatusSelected() {
      return this.filters.status?.id === Saee10StatusEnum.Pending;
    },
  },
  methods: {
    persistFilters() {
      sessionStorage.setItem(FILTERS_KEY, JSON.stringify(this.filters));
      sessionStorage.setItem(PAGINATION_KEY, JSON.stringify(this.pagination));
    },
    async loadCenters(academicYearId) {
      let id = academicYearId;
      if (!id) {
        id = this.filters.academicYear.id;
      }
      const { data } = await locationService.getCenters({
        academicYearId: id,
      });
      this.centers = data;

      if (data != null && data.length == 1 && data[0].selected) {
        this.filters.center = data[0];
        await this.loadMunicipalities(this.filters.center.id);
      }
    },
    async loadMunicipalities(centerId) {
      const { data } = await locationService.getMunicipalities({
        centerId,
        academicYearId: this.filters.academicYear.id,
      });
      this.municipalities = data;

      if (data != null && data.length == 1 && data[0].selected) {
        this.filters.municipality = data[0];
        await this.loadSchools(this.filters.municipality.id);
      }
    },
    async loadSchools(municipalityId) {
      const { data } = await locationService.getSchools({
        municipalityId,
        academicYearId: this.filters.academicYear.id,
      });
      this.schools = data;

      if (data != null && data.length == 1 && data[0].selected) {
        this.filters.school = data[0];
        await this.loadAssistants(this.filters.school.id);
      }
    },
    async loadAssistants(schoolId) {
      const academicYearId = this.filters.academicYear.id;
      const { data } = await GetAssistantList(academicYearId, schoolId);
      this.assistants = data;
    },
    async loadNecesities(academicYearId) {
      const { data } = await GetNecesitiesByYear(academicYearId);
      this.collections.necesities = data;
    },
    async onAcademicYearChanged() {
      this.filters.status = null;
      this.filters.searchField = null;

      this.filters.center = this.centers.length === 1 ? this.centers[0] : null;
      this.filters.municipality =
        this.municipalities.length === 1 ? this.municipalities[0] : null;
      this.filters.school = this.schools.length === 1 ? this.schools[0] : null;

      this.filters.requestType = null;
      this.filters.necesity = null;
      this.filters.ubicationAlternative = null;
      this.filters.route = null;
      this.filters.assistant = null;
      this.filters.assignmentStatus = null;
      await this.loadNecesities(this.filters.academicYear.id);
      if (this.filters.school) {
        this.loadAssistants(this.filters.school.id);
      }

      this.$emit('changeAcademicYear', this.filters.academicYear.id);
    },
    onCenterChanged(center) {
      this.filters.municipality = null;
      this.municipalities = [];
      this.onMunicipalityChanged(); // Cleaning schools
      if (center) {
        this.loadMunicipalities(center.id);
      }
    },
    onMunicipalityChanged(municipality) {
      this.filters.school = null;
      this.schools = [];
      if (municipality) {
        this.loadSchools(municipality.id);
      }
    },
    onSchoolChanged(school) {
      this.filters.assistant = null;
      this.assistants = [];
      if (school) {
        this.loadAssistants(school.id);
      }
    },
    onStatusChanged() {
      this.filters.assignmentType = null;
    },
    async onSearch(filters, page) {
      this.getResults(filters, page);
    },
    initResults(filters, page) {
      this.getResults(filters, page);
    },
    async getResults(filters, page = 1) {
      const mappedFilters = {
        academicYearId: Number(filters.academicYear.id),
        statusId: filters.status ? Number(filters.status.id) : null,
        searchField: filters.searchField,
        centerId: filters.center?.id,
        municipalityId: filters.municipality?.id,
        schoolId: filters.school?.id,
        necesityId: filters.necesity?.id,
        ubicationAlternativeId: filters.ubicationAlternative?.id,
        assistantId: filters.assistant?.id,
        assignmentStatus: filters.assignmentStatus?.id,
        assignmentTypeId: filters.assignmentType?.id,
        pageIndex: page,
      };

      const { data } = await GetRequest(mappedFilters);

      this.results = data.data.map((row) => this.normalizeGroupedRow(row));
      this.pagination = {
        ...this.pagination,
        totalItemCount: data.totalItemCount,
        pageCount: data.pageCount,
        currentPage: data.currentPage,
        count: data.count,
      };
      this.persistFilters();
    },
    async onPageChanged(currentPage) {
      this.pagination.currentPage = currentPage;
      await this.onSearch(this.filters, currentPage);
    },
    async assignAssistant(saee10Id, assistantIds, alternativeLocationTeacherId) {
      await AssignAssistant(saee10Id, assistantIds, alternativeLocationTeacherId);
      this.updateAssignedAssistantRow(saee10Id, assistantIds);

      this.ShowToast(
        'Proceso Exitoso.',
        'La información ha sido guardada exitosamente.',
        'success'
      );
    },
    buildAssignedAssistants(detail, assistantIds) {
      const options = detail.assignmentOptions || [];
      return assistantIds
        .map(
          (id) =>
            options.find((x) => x.isAssistant && x.key == id) ||
            (detail.assistants || []).find((x) => (x.id || x.Id) == id) ||
            (detail.assistant &&
            (detail.assistant.id || detail.assistant.Id) == id
              ? detail.assistant
              : null)
        )
        .filter(Boolean)
        .map((assistant) => ({
          id: assistant.key || assistant.id || assistant.Id,
          name: assistant.value || assistant.name || assistant.Name,
        }));
    },
    isPendingSaee10(statusId) {
      return [
        Saee10StatusEnum.Pending,
        Saee10StatusEnum.Process,
        Saee10StatusEnum.RecommendedPendingAssignment,
        Saee10StatusEnum.NotRecommendedPendingAssignment,
        Saee10StatusEnum.InProcessPendingAssignement,
      ].includes(Number(statusId));
    },
    isPendingSaee10Detail(detail) {
      if (this.isPendingSaee10(detail.statusId)) return true;

      return (
        Number(detail.statusId) ===
          Saee10StatusEnum.ApprovedProvisionalRemedy &&
        !this.hasAssignedAssistant(detail)
      );
    },
    hasAssignedAssistant(detail) {
      return (
        !!detail.assistantId ||
        !!detail.assistant ||
        !!detail.assigmentTypeId ||
        (Array.isArray(detail.assistants) && detail.assistants.length > 0)
      );
    },
    normalizeGroupedRow(row) {
      const details =
        row.saee10Details && row.saee10Details.length
          ? row.saee10Details
          : [row];

      return this.rebuildGroupedRow(row, details);
    },
    rebuildGroupedRow(row, updatedDetails) {
      const isPending = updatedDetails.some((detail) =>
        this.isPendingSaee10Detail(detail)
      );
      const necesities = [
        ...new Set(
          updatedDetails
            .flatMap((detail) => (detail.necesities || '').split(','))
            .map((value) => value.trim())
            .filter(Boolean)
        ),
      ].join(', ');

      return {
        ...row,
        status: isPending ? 'Pendiente' : 'Atendido',
        statusColor: isPending ? '#FFF077' : '#A3E5B3',
        necesities,
        saee10Details: updatedDetails,
      };
    },
    updateAssignedAssistantRow(saee10Id, assistantIds) {
      const selectedAssistantIds = Array.isArray(assistantIds)
        ? assistantIds
        : [assistantIds];

      const rowIndex = this.results.findIndex((row) => {
        const details =
          row.saee10Details && row.saee10Details.length
            ? row.saee10Details
            : [row];
        return details.some((detail) => detail.id == saee10Id);
      });

      if (rowIndex < 0) return;

      const row = this.results[rowIndex];
      const details =
        row.saee10Details && row.saee10Details.length
          ? row.saee10Details
          : [row];
      const updatedDetails = details.map((detail) => {
        if (detail.id != saee10Id) return detail;

        const assistants = this.buildAssignedAssistants(
          detail,
          selectedAssistantIds
        );
        const hasAssistants = assistants.length > 0;

        return {
          ...detail,
          assistantId: hasAssistants ? assistants[0].id : null,
          assistant: hasAssistants
            ? { id: assistants[0].id, name: assistants[0].name }
            : null,
          assistants,
          assigmentTypeId: hasAssistants ? AssignmentTypeEnum.Assistant : null,
          statusId: hasAssistants
            ? Saee10StatusEnum.RecommendedExistentResource
            : Saee10StatusEnum.Pending,
          status: hasAssistants
            ? 'Recomendado - Recurso Existente'
            : 'Pendiente de Asignar',
          statusColor: hasAssistants ? '#A3E5B3' : '#FFF077',
        };
      });

      this.$set(
        this.results,
        rowIndex,
        this.rebuildGroupedRow(row, updatedDetails)
      );
    },
    updateAssignmentTypeRow(saee10Id, assignmentTypeId) {
      const rowIndex = this.results.findIndex((row) => {
        const details =
          row.saee10Details && row.saee10Details.length
            ? row.saee10Details
            : [row];
        return details.some((detail) => detail.id == saee10Id);
      });

      if (rowIndex < 0) return;

      const row = this.results[rowIndex];
      const details =
        row.saee10Details && row.saee10Details.length
          ? row.saee10Details
          : [row];
      const updatedDetails = details.map((detail) => {
        if (detail.id != saee10Id) return detail;

        return {
          ...detail,
          assistantId: null,
          assistant: null,
          assistants: [],
          assigmentTypeId: assignmentTypeId,
          statusId: Saee10StatusEnum.Pending,
          status: 'Pendiente de Asignar',
          statusColor: '#FFF077',
        };
      });

      this.$set(
        this.results,
        rowIndex,
        this.rebuildGroupedRow(row, updatedDetails)
      );
    },
    async changeToPendingAssign(saee10Id) {
      await ChangeToPendingAssign(saee10Id);

      this.ShowToast(
        'Proceso Exitoso.',
        'La información ha sido guardada exitosamente.',
        'success'
      );

      await this.getResults(this.filters, this.pagination.currentPage);
    },
    async setAsPendingAmendment(saee10Id) {
      await SetAsPendingAmendment(saee10Id);
      this.updateAssignmentTypeRow(
        saee10Id,
        AssignmentTypeEnum.PendingAmendment
      );

      this.ShowToast(
        'Proceso Exitoso.',
        'La información ha sido guardada exitosamente.',
        'success'
      );
    },
    async setAsVacantPosition(saee10Id) {
      await SetAsVacantPosition(saee10Id);
      this.updateAssignmentTypeRow(saee10Id, AssignmentTypeEnum.VacantPosition);

      this.ShowToast(
        'Proceso Exitoso.',
        'La información ha sido guardada exitosamente.',
        'success'
      );
    },
    async cancelRequest(saee10Id, reason) {
      await CancelRequest(saee10Id, reason);
      this.ShowToast(
        'Proceso exitoso',
        'Su información ha sido guardada',
        'success'
      );

      await this.onSearch(this.filters, this.pagination.currentPage);
    },
    async configurationResult() {
      const savedFilters = sessionStorage.getItem(FILTERS_KEY);
      const savedPagination = sessionStorage.getItem(PAGINATION_KEY);

      if (savedFilters) {
        this.filters = JSON.parse(savedFilters);
        sessionStorage.removeItem(FILTERS_KEY);
      } else {
        this.filters.academicYear =
          this.collections.academicYears.find((e) => e.selected) ??
          this.collections.academicYears[0];
      }

      this.$emit('changeAcademicYear', this.filters.academicYear.id);

      if (savedPagination) {
        this.pagination = JSON.parse(savedPagination);
        sessionStorage.removeItem(PAGINATION_KEY);
      }
      await this.loadNecesities(this.filters.academicYear?.id);
      await this.loadCenters(this.filters.academicYear?.id);
      this.initResults(this.filters, this.pagination.currentPage);
    },
  },
  async mounted() {
    this.configurationResult();
  },
  watch: {
    filters: {
      handler(newVal) {
        this.$emit('validateIfNeedResource', newVal);
      },
      deep: true,
    },
  },
};
</script>
